import { useEffect, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Button as MuiButton,
  Collapse,
  Dialog,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ExpandLess, ExpandMore, Lock } from '@mui/icons-material';

import { selectedDeviceAtom } from './MapView';
import { fdaEnabledAtom, selectedMenuAtom } from './Sidebar';

import type { MenuItemData } from '@/@types/types';

export interface MenuProps {
  item: MenuItemData;
}

export const Button = styled(ListItemButton)(
  ({ theme }) => `
  &.Mui-selected {
    color: ${theme.palette.primary.main};
    background-color: '#F3F4F6';
    & svg {
      color: ${theme.palette.primary.main};
    }
  }
  `,
);
export const Icon = styled(ListItemIcon)`
  color: black;
`;
export const Text = styled(ListItemText)`
  & .MuiListItemText-primary {
    font-weight: 500;
  }
`;

export function MenuItem({ item }: MenuProps) {
  const { text, icon, path } = item;

  const [selectedMenu, setSelectedMenu] = useAtom(selectedMenuAtom);
  const setSelectedDevice = useSetAtom(selectedDeviceAtom);
  const navigate = useNavigate();

  function onMenuItemClick(path: string) {
    setSelectedDevice(null);
    setSelectedMenu(path);
    navigate(path, { replace: false });
  }

  return (
    <ListItem key={text} disablePadding>
      <Button selected={selectedMenu === path} onClick={() => onMenuItemClick(path)}>
        <Icon>{icon}</Icon>
        <Text>{text}</Text>
      </Button>
    </ListItem>
  );
}

export function MenuItemWithChild({ item }: MenuProps) {
  const { text, icon, path, child, disabled } = item;

  const [selectedMenu, setSelectedMenu] = useAtom(selectedMenuAtom);
  const setSelectedDevice = useSetAtom(selectedDeviceAtom);

  const [isOpen, setIsOpen] = useState(selectedMenu.startsWith(path));
  const [showDialog, setShowDialog] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsOpen(selectedMenu.startsWith(path));
  }, [path, selectedMenu]);

  function onMenuClick() {
    if (disabled) {
      setShowDialog(true);
    } else {
      setIsOpen(!isOpen);
    }
  }
  function onMenuItemClick(path: string) {
    setSelectedDevice(null);
    setSelectedMenu(path);
    navigate(path, { replace: false });
  }
  function onDialogClose() {
    setShowDialog(false);
  }

  return (
    <>
      <ListItem disablePadding>
        <Button disableRipple onClick={onMenuClick} sx={disabled ? { '*': { color: 'gray' } } : {}}>
          <Icon>{icon}</Icon>
          <Text>{text}</Text>
          {disabled ? <Lock /> : isOpen ? <ExpandLess /> : <ExpandMore />}
        </Button>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List disablePadding>
          {child.map(({ text, path }) => (
            <ListItem key={text} disablePadding>
              <Button selected={selectedMenu === path} onClick={() => onMenuItemClick(path)}>
                <ListItemIcon />
                <Text>{text}</Text>
              </Button>
            </ListItem>
          ))}
        </List>
      </Collapse>
      <Dialog open={showDialog} onClose={onDialogClose}>
        <AdvertisementDialog onClose={onDialogClose} />
      </Dialog>
    </>
  );
}

function AdvertisementDialog({ onClose }: { onClose: () => void }) {
  const setFdaEnabled = useSetAtom(fdaEnabledAtom);
  const { t } = useTranslation();

  function onUpgradeClick() {
    // TODO: Integrate with API
    setFdaEnabled(true);
    onClose();
  }

  return (
    <Stack gap={2} alignItems="center" sx={{ p: 6 }}>
      <Lock sx={{ fontSize: 72 }} />
      <Typography textAlign="center" whiteSpace="pre-line">
        {t('FDA.message.needUpgrade')}
      </Typography>
      <MuiButton variant="contained" onClick={onUpgradeClick}>
        {t('Devices.label.upgrade')}
      </MuiButton>
    </Stack>
  );
}
